import { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toPng } from 'html-to-image';
import { saveAs } from 'file-saver';
import { brandConfig } from '../../../config/brands';
import styles from './GenerationInfoModal.module.css';
import type { MaterialData } from './types';
import { parseGenerationMetadata, resolveAttributeLabels, TEXT_ATTRIBUTE_KEYS } from './generationInfoUtils';
import { usePromptModels } from '../../generation/hooks/usePromptModels';
import { useGenerationModels } from '../../../features/generation-models/hooks';
import { PROVIDER_META } from '../../generation/common/VideoModelFamilySelector';
import {
  gptImageFamilyLabelFromCode,
  gptImageQuality,
  isGptImageModelCode,
  stripGptPrefix,
} from '../../generation/utils/imageModelFamily';
import { useSeedancePresetActors } from '../../../features/seedance-preset-actors';
import { usePromptAttributes } from '../../generation/hooks/usePromptAttributes';
import { useLockBodyScroll } from '../../../hooks/useLockBodyScroll';
import { useComplianceAIReports } from '../../../features/compliance/api';
import { audioModelLabel, audioProviderLabel } from '../../../features/materials/audioModelLabels';
import { layoutFromGenerationMetadata, reuseLayoutSettings } from '../../../features/bannerLayout/reuse';
import { useBannerLayoutEditorEnabled } from '../../../features/bannerLayout/useBannerLayoutEditorEnabled';
import { useSafeGenerationState, useSafeLocation, useSafeNavigate } from '../../../hooks/useSafeRouter';
import { useToast } from '../../../hooks/useToast';

type Props = {
  material: MaterialData;
  fullSizeImageUrl: string | null;
  onClose: () => void;
  /** 「レイアウトを再利用」で静止画の生成画面へ移った後に呼ぶ (素材プレビュー等の親モーダルを閉じる) */
  onNavigateToGeneration?: () => void;
};

/* 矢印コンポーネント（繰り返し使うため抽出） */
const ArrowH = () => (
  <div className={styles.arrow}>
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  </div>
);

const ArrowV = () => (
  <div className={styles.arrowVertical}>
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v14" />
      <path d="m19 12-7 7-7-7" />
    </svg>
  </div>
);

/** 画像読み込みエラー時のプレースホルダ */
const ImageWithFallback = ({
  src,
  alt,
  className,
}: {
  src: string;
  alt: string;
  className?: string;
}) => {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return <div className={styles.imagePlaceholder}>削除されました</div>;
  }
  return <img src={src} alt={alt} className={className} onError={() => setFailed(true)} />;
};

/** エクスポート幅 (HTMLメール標準 600px, Retina 2x) */
const EXPORT_PIXEL_RATIO = 2;

/** 景品表示法は括弧内の補足説明（優良誤認・有利誤認等）を省略して表示する */
const formatComplianceRuleName = (name: string): string => {
  if (/^景品表示法[（(]/.test(name)) return '景品表示法';
  return name;
};

/** 画像生成モデルの品質ティア (model_code 接尾辞 / provider_response.quality) の和名。
 *  xhigh / max は GPT Image 2.5 系で追加された上位品質 (2026-09-09 に UI でも解放)。 */
const QUALITY_JP: Record<string, string> = {
  high: '高品質',
  medium: '標準',
  low: '低品質',
  auto: '自動',
  xhigh: '超高品質',
  max: '最高品質',
};

/**
 * 画像生成モデルのコード → AI モデル名 (ファミリー)。
 * generation_models.model_family_name を正本にし、行が引けない旧モデル・非表示モデルは
 * model_code からのヒューリスティックで補う。
 * 例: gpt-image-2-high → "GPT Image 2" / gpt-image-2.5-flare-auto → "GPT Image 2.5 Flare" /
 *     gemini-nano-banana-2.1 → "Gemini Nano Banana 2.1" / gemini-3.1-flash-image-preview (廃止) → "Gemini 3.1 Flash Image"
 */
const imageModelFamilyLabel = (
  modelCode: string,
  models: { model_code: string; model_family_name: string | null }[],
): string => {
  const row = models.find((m) => m.model_code === modelCode);
  if (row?.model_family_name) return row.model_family_name;
  if (isGptImageModelCode(modelCode)) {
    return gptImageFamilyLabelFromCode(modelCode);
  }
  if (modelCode.startsWith('gemini-')) {
    // gemini / image / preview は表示上のノイズなので落とし、残りを Capitalize して連結する
    const words = modelCode.split('-').filter((w) => w && w !== 'gemini' && w !== 'image' && w !== 'preview');
    return ['Gemini', ...words.map((w) => w.charAt(0).toUpperCase() + w.slice(1))].join(' ');
  }
  return modelCode;
};

/**
 * 画像生成モデルの品質ティア名 (高品質 / 標準 / スピード 等)。
 * generation_models.display_label を正本にし ("GPT高品質" → "高品質")、
 * 行が引けない旧モデル・非表示モデルは model_code からのヒューリスティックで補う。
 */
const imageModelTierLabel = (
  modelCode: string,
  providerQuality: string | null,
  models: { model_code: string; display_label: string }[],
): string | null => {
  // quality='auto' でリクエストした場合、OpenAI が実際に選んだ品質が provider_response.quality に
  // 記録されるため、'自動' ではなく選ばれた実値を表示する。
  if (modelCode.endsWith('-auto') && providerQuality && QUALITY_JP[providerQuality]) {
    return QUALITY_JP[providerQuality];
  }
  const row = models.find((m) => m.model_code === modelCode);
  if (row?.display_label) return stripGptPrefix(row.display_label);
  const suffix = isGptImageModelCode(modelCode) ? gptImageQuality(modelCode) : null;
  if (suffix) return QUALITY_JP[suffix];
  // Gemini 3 ティア: 高品質 (Pro) / 標準 (3.1 Flash) / スピード (3.1 Flash Lite)。
  // レガシー (gemini-2.5-flash-image 等) は旧スピード枠のため「スピード」に寄せる。
  if (modelCode.startsWith('gemini-')) {
    if (modelCode.includes('pro')) return '高品質';
    if (modelCode.includes('flash-lite')) return 'スピード';
    if (modelCode.startsWith('gemini-3.1-flash-image')) return '標準';
    return 'スピード';
  }
  // 品質接尾辞のない旧 gpt-image (gpt-image-1.5 等) や未知のコードはティアを出さない
  return null;
};

export function GenerationInfoModal({ material, fullSizeImageUrl, onClose, onNavigateToGeneration }: Props) {
  useLockBodyScroll(true);

  const flowRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);

  const materialRecord = material as Record<string, unknown>;
  const metadata = materialRecord.generation_metadata as Record<string, unknown> | undefined;
  const parsed = useMemo(() => parseGenerationMetadata(metadata ?? null), [metadata]);

  // コンプライアンス AI 判定結果を取得
  const materialIdForReport = (materialRecord.id as string) || null;
  const folderType = (materialRecord.folder_type as string) || '';
  const reportTable: 'temporary_materials' | 'materials' =
    folderType === 'cloud' ? 'materials' : 'temporary_materials';
  const { data: complianceReports } = useComplianceAIReports(materialIdForReport, reportTable);
  const latestReport = complianceReports && complianceReports.length > 0 ? complianceReports[0] : null;

  // 「レイアウトを再利用」(バナーレイアウト §4): generation_metadata.banner_layout から settings.banner_layout と比率を復元する
  const generationState = useSafeGenerationState();
  const layoutEditorEnabled = useBannerLayoutEditorEnabled();
  const navigate = useSafeNavigate();
  const location = useSafeLocation();
  const toast = useToast();
  const reusableLayout = useMemo(() => layoutFromGenerationMetadata(metadata?.banner_layout), [metadata]);
  const handleReuseLayout = () => {
    if (!generationState || !reusableLayout) return;
    generationState.setSettings((prev) => ({ ...prev, ...reuseLayoutSettings(prev, reusableLayout) }));
    // レイアウトの入口は属性モードだけ
    try {
      localStorage.setItem('studio_still_image_mode', 'attributes');
    } catch {
      // localStorage が使えなくても遷移はする
    }
    if (location.pathname !== '/studio/image') navigate('/studio/image');
    toast.show('レイアウトを読み込みました');
    onClose();
    onNavigateToGeneration?.();
  };

  const contentType = (materialRecord.content_type as string) || (materialRecord.contentType as string) || '';
  const isVideo = parsed?.isVideo || contentType.startsWith('video/');
  const isAudio = Boolean(parsed?.isAudio) || contentType.startsWith('audio/');

  // 動画生成モデルのコード → ファミリー名(動画生成モデル) + ティア名(生成モード) に解決する。
  // generation_metadata.model には BytePlus ID 等の provider_endpoint が入るため、model_code
  // 完全一致 → provider_endpoint 完全一致 → model_code 前方一致 の順で generation_models 行を引く。
  // ファミリー名は model_family_name (「Seedance 2.0」「Veo 3.1」形) を正本に、未設定行は
  // PROVIDER_META のラベルからベンダー接頭辞 (ByteDance / Google 等) を落として補う。
  // ティア名は display_label (標準 / スピード / ミニ) に対応する。
  const { data: videoModels = [] } = useGenerationModels('video');
  const resolveVideoModel = useCallback((code: string | null): { family: string; tier: string | null } | null => {
    if (!code) return null;
    const VENDOR_PREFIXES = ['ByteDance ', 'Google ', 'Kuaishou '];
    const stripVendor = (label: string) => {
      for (const v of VENDOR_PREFIXES) {
        if (label.startsWith(v)) return label.slice(v.length);
      }
      return label;
    };
    const m =
      videoModels.find((x) => x.model_code === code) ||
      videoModels.find((x) => x.provider_endpoint === code) ||
      videoModels
        .filter((x) => code.startsWith(x.model_code))
        .sort((a, b) => b.model_code.length - a.model_code.length)[0] ||
      null;
    if (!m) return { family: code, tier: null };
    const providerLabel = m.provider ? PROVIDER_META[m.provider]?.label : undefined;
    const family = m.model_family_name || stripVendor(providerLabel || m.display_label || code);
    return { family, tier: m.display_label || null };
  }, [videoModels]);

  // 画像生成モデルは品質ティア名 (display_label) の解決に使う
  const { data: imageModels = [] } = useGenerationModels('image');

  // 属性ツリーを取得してラベル解決に使用
  const { data: attributesData } = usePromptAttributes();
  const attributeTree = attributesData?.items;

  // AI俳優: 素材レコードの model_ids[] を正本に複数解決。旧データは generation_metadata の
  // 単数ミラー (parsed.modelIds) にフォールバック。順序 = 登場人物A/B… を保持する。
  const actorModelIds = useMemo<string[]>(() => {
    const fromRecord = Array.isArray(materialRecord.model_ids)
      ? (materialRecord.model_ids as unknown[]).filter((m): m is string => typeof m === 'string' && Boolean(m))
      : [];
    if (fromRecord.length > 0) return fromRecord;
    return parsed?.modelIds ?? [];
  }, [materialRecord.model_ids, parsed]);

  // model_ids[] は通常AI俳優 (prompt_models) と Seedance preset actor の id が混在しうるため、
  // 両方を解決する (片方だけでは preset 俳優が空表示で握り潰される — Phase4 受け入れ条件)。
  const { data: promptActorModels } = usePromptModels(
    { ids: actorModelIds },
    { enabled: actorModelIds.length > 0 },
  );
  // 生成履歴は「生成時に使った actor」を表示するため、後から非アクティブ化された preset も
  // 解決できるよう active_only=false で取得する (active_only 既定 true だと表示から消える)。
  const { data: presetActorsResp } = useSeedancePresetActors(
    { active_only: false },
    { enabled: actorModelIds.length > 0 },
  );

  // model_ids[] の順序を保って俳優表示情報へ正規化。解決できない id (削除済み等) は除外する。
  const actors = useMemo(() => {
    if (actorModelIds.length === 0) return [] as Array<{ id: string; name: string; faceUrl?: string; characterSheetUrl?: string }>;
    const promptById = new Map((promptActorModels ?? []).map((m) => [m.id, m]));
    const presetById = new Map((presetActorsResp?.actors ?? []).map((a) => [a.id, a]));
    const out: Array<{ id: string; name: string; faceUrl?: string; characterSheetUrl?: string }> = [];
    for (const id of actorModelIds) {
      const pm = promptById.get(id);
      if (pm) {
        out.push({ id, name: pm.display_name || pm.name, faceUrl: pm.face_image_public_url, characterSheetUrl: pm.character_sheet_public_url });
        continue;
      }
      const ps = presetById.get(id);
      if (ps) {
        out.push({ id, name: ps.display_name, faceUrl: ps.thumbnail_public_url ?? undefined });
      }
    }
    return out;
  }, [actorModelIds, promptActorModels, presetActorsResp]);

  const META_KEYS = new Set(['media_spec', 'aspect_ratio']);

  const { attributes, metaAttributes } = useMemo(() => {
    if (!parsed) return { attributes: [], metaAttributes: [] };

    // 動画素材のメタ行を統一順序で構築する。
    // 順序: 動画生成モデル → 生成モード → アスペクト比 → 解像度 → 動画の長さ → 生成所要時間。
    // 値は parsed を優先し、未設定 (parse が _app 形式を優先する素材) は生メタ top-level で補完する。
    const buildVideoMetaRows = () => {
      const rows: { key: string; label: string; value: string; section: number }[] = [];
      const code = parsed.generationModel ?? (typeof metadata?.model === 'string' ? metadata.model : null);
      const vm = resolveVideoModel(code);
      if (vm) {
        rows.push({ key: 'video_model', label: '動画生成モデル', value: vm.family, section: 1 });
        if (vm.tier) {
          rows.push({ key: 'generation_info', label: '生成モード', value: vm.tier, section: 1 });
        }
      }
      const aspect = (typeof parsed.attributes?.aspect_ratio === 'string' && parsed.attributes.aspect_ratio)
        || (typeof metadata?.aspect_ratio === 'string' ? metadata.aspect_ratio : null);
      if (aspect) {
        rows.push({ key: 'aspect_ratio', label: 'アスペクト比', value: aspect, section: 1 });
      }
      const resolution = parsed.videoResolution ?? (typeof metadata?.resolution === 'string' ? metadata.resolution : null);
      if (resolution) {
        rows.push({ key: 'video_resolution', label: '解像度', value: resolution, section: 1 });
      }
      const duration = parsed.videoDuration ?? (typeof metadata?.duration_seconds === 'number' ? metadata.duration_seconds : null);
      if (duration !== null) {
        rows.push({ key: 'video_duration', label: '動画の長さ', value: `${duration}秒`, section: 1 });
      }
      const elapsed = parsed.generationSeconds ?? (typeof metadata?.generation_seconds === 'number' ? metadata.generation_seconds : null);
      if (elapsed !== null) {
        rows.push({ key: 'video_elapsed', label: '生成所要時間', value: `${Math.round(elapsed)}秒`, section: 1 });
      }
      return rows;
    };

    // 動画生成の場合: 動画固有のメタ情報を構築
    if (parsed.isVideo) {
      return { attributes: [], metaAttributes: buildVideoMetaRows() };
    }

    // 音声生成 (BGM / ナレーション) の場合: 生成モデル → プロバイダ → 曲の設定 → 尺。
    // モデル名は generation_models 行を持たない (音声モデルは DB 管理外) ため
    // audioModelLabels のヒューリスティックで表示名に整形する。
    if (parsed.isAudio && parsed.audioInfo) {
      const a = parsed.audioInfo;
      const rows: { key: string; label: string; value: string; section: number }[] = [];
      const modelLabel = audioModelLabel(a.model);
      if (modelLabel) {
        rows.push({ key: 'audio_model', label: a.kind === 'bgm' ? '音楽生成モデル' : '音声生成モデル', value: modelLabel, section: 1 });
      }
      const providerLabel = audioProviderLabel(a.provider);
      if (providerLabel) {
        rows.push({ key: 'audio_provider', label: 'プロバイダ', value: providerLabel, section: 1 });
      }
      if (a.kind === 'bgm') {
        if (a.songTitle) rows.push({ key: 'song_title', label: '曲名', value: a.songTitle, section: 2 });
        if (a.isInstrumental !== null) {
          rows.push({ key: 'vocal', label: 'ボーカル', value: a.isInstrumental ? 'なし (インスト)' : 'あり', section: 2 });
        }
        if (a.genre) rows.push({ key: 'genre', label: 'ジャンル', value: a.genre, section: 2 });
        if (a.mood) rows.push({ key: 'mood', label: 'ムード', value: a.mood, section: 2 });
        if (a.bpm !== null) rows.push({ key: 'bpm', label: 'BPM', value: String(a.bpm), section: 2 });
        if (a.musicalKey) rows.push({ key: 'musical_key', label: 'キー', value: a.musicalKey, section: 2 });
        if (a.requestedDurationSeconds !== null) {
          rows.push({ key: 'requested_duration', label: '指定した長さ', value: `${a.requestedDurationSeconds}秒`, section: 3 });
        }
      }
      if (a.actualDurationMs !== null) {
        rows.push({ key: 'audio_duration', label: a.kind === 'bgm' ? '曲の長さ' : '音声の長さ', value: `${Math.round(a.actualDurationMs / 1000)}秒`, section: 3 });
      }
      return { attributes: [], metaAttributes: rows };
    }

    // 画像生成の場合: 既存ロジック
    const excludeTextResponse = parsed.settingsSnapshot?.exclude_text_response === true;
    const items = resolveAttributeLabels(parsed.attributes, attributeTree, { excludeTextResponse });
    if (parsed.mediaSpecLabel) {
      items.push({ key: 'media_spec', label: '媒体要件', value: parsed.mediaSpecLabel, section: 4 });
    }
    const main = items.filter((i) => !META_KEYS.has(i.key));
    const meta = items.filter((i) => META_KEYS.has(i.key));
    // 動画素材（_app 形式で duration_seconds を持たないもの）もこの画像パスを通る。
    // その場合は画像用メタ (アスペクト比/媒体要件 + 生成モード) を捨てて、動画用メタ行に統一する。
    // 画像用のティア解決 (スピード/高品質) は動画には当てない。
    if (isVideo) {
      return { attributes: main, metaAttributes: buildVideoMetaRows() };
    }
    // 生成モデル・秒数もメタ行に追加。AI モデル名が分かるよう
    // 「モデル名 + 品質ティア」で表示する (例: "GPT Image 2 高品質 / 12秒")。
    const modelLabel = parsed.generationModel
      ? [
          imageModelFamilyLabel(parsed.generationModel, imageModels),
          imageModelTierLabel(parsed.generationModel, parsed.providerQuality, imageModels),
        ]
          .filter(Boolean)
          .join(' ')
      : null;
    const secs = parsed.generationSeconds !== null ? `${Math.round(parsed.generationSeconds)}秒` : null;
    const genParts = [modelLabel, secs].filter(Boolean).join(' / ');
    if (genParts) {
      meta.push({ key: 'generation_info', label: '生成モード', value: genParts, section: 5 });
    }
    // プロンプト入力モードの場合、入力モードをメタ行に追加
    if (parsed.mode === 'prompt') {
      meta.push({ key: 'input_mode', label: '入力モード', value: 'プロンプト入力', section: 5 });
    }
    return { attributes: main, metaAttributes: meta };
  }, [parsed, attributeTree, isVideo, resolveVideoModel, metadata, imageModels]);

  // キャラクターシート・AI俳優画像を参照画像から除外（顔写真は属性列で表示）。
  // 絵コンテ(ストーリーボード)では backend 側でキャラクターシートを生成情報フィールドに
  // 含めないため、ここに AI俳優は流れてこない (生成元の画像に焼き込まない方針)。
  const ACTOR_IMAGE_PREFIX = 'https://lirsarznxaidebqhgxch.supabase.co/storage/v1/object/public/model-images/actors/';
  // 全俳優のキャラクターシートURLを参照画像から除外（顔写真は属性列で表示）。
  const actorCsUrls = useMemo(
    () => new Set(actors.map((a) => a.characterSheetUrl).filter((u): u is string => Boolean(u))),
    [actors],
  );
  const referenceImages = useMemo(() => {
    const all = parsed?.referenceImages ?? [];
    return all.filter((ref) => {
      if (actorCsUrls.has(ref.url)) return false;
      if (ref.label && ref.label.includes('キャラクターシート')) return false;
      if (ref.url.startsWith(ACTOR_IMAGE_PREFIX)) return false;
      return true;
    });
  }, [parsed, actorCsUrls]);
  const referenceVideos = parsed?.referenceVideos ?? [];

  // 表示するメディアURL
  const generatedMediaUrl = fullSizeImageUrl
    || materialRecord.preview_url as string | undefined
    || materialRecord.previewUrl as string | undefined
    || materialRecord.thumbnail_url as string | undefined
    || materialRecord.thumbnailUrl as string | undefined
    || materialRecord.source_url as string | undefined
    || materialRecord.sourceUrl as string | undefined
    || null;

  const sceneEntries = parsed?.scenePrompt ?? null;
  // ストーリーボードはコマ単位のプロンプトを履歴カードに載せない（シーンスクリプト/属性のみ表示）
  const isStoryboard = (materialRecord.source_type as string | undefined) === 'scenario_storyboard';
  const directPrompt = isStoryboard ? null : (parsed?.directPrompt ?? null);
  const complianceRules = parsed?.compliance?.applied_rulesets ?? [];
  const hasCompliance = complianceRules.length > 0;
  const hasAIReport = Boolean(latestReport);
  const hasSources = referenceImages.length > 0 || referenceVideos.length > 0;
  const hasAttributes = attributes.length > 0 || metaAttributes.length > 0 || actors.length > 0 || Boolean(sceneEntries) || Boolean(directPrompt) || hasCompliance || hasAIReport;

  // 動画のサムネイルURL（エクスポート時に <video> の代わりに使用）
  const videoThumbnailUrl = isVideo
    ? (materialRecord.thumbnail_url as string | undefined)
      || (materialRecord.thumbnailUrl as string | undefined)
      || (materialRecord.preview_url as string | undefined)
      || (materialRecord.previewUrl as string | undefined)
      || null
    : null;

  /* PNG エクスポート処理 */
  const handleExport = useCallback(async () => {
    const el = flowRef.current;
    if (!el || exporting) return;
    setExporting(true);

    // html-to-image は <video> をレンダリングできないため、
    // エクスポート前にサムネイル画像の <img> に差し替える
    let videoEl: HTMLVideoElement | null = null;
    let placeholderImg: HTMLImageElement | null = null;
    // 参照動画 (サムネイル無し) はテキストプレースホルダに差し替える
    const refVideoSwaps: Array<{ video: HTMLVideoElement; placeholder: HTMLElement }> = [];
    const restoreRefVideos = () => {
      for (const { video, placeholder } of refVideoSwaps) {
        placeholder.parentElement?.replaceChild(video, placeholder);
      }
      refVideoSwaps.length = 0;
    };
    try {
      el.querySelectorAll<HTMLVideoElement>('video[data-ref-video]').forEach((v) => {
        const ph = document.createElement('div');
        ph.textContent = '参照動画';
        ph.style.display = 'flex';
        ph.style.alignItems = 'center';
        ph.style.justifyContent = 'center';
        ph.style.width = '80px';
        ph.style.height = '48px';
        ph.style.fontSize = '10px';
        ph.style.color = '#666';
        ph.style.background = '#f5f5f5';
        ph.style.border = '1px solid #e8e8e8';
        ph.style.borderRadius = '4px';
        v.parentElement?.replaceChild(ph, v);
        refVideoSwaps.push({ video: v, placeholder: ph });
      });

      videoEl = el.querySelector('video[data-generated-video]');
      if (videoEl && videoThumbnailUrl) {
        placeholderImg = document.createElement('img');
        placeholderImg.src = videoThumbnailUrl;
        placeholderImg.crossOrigin = 'anonymous';
        placeholderImg.style.maxWidth = '100%';
        placeholderImg.style.maxHeight = '50vh';
        placeholderImg.style.objectFit = 'contain';
        placeholderImg.style.borderRadius = '4px';
        placeholderImg.style.border = '1px solid #e8e8e8';
        videoEl.parentElement?.replaceChild(placeholderImg, videoEl);
      }

      // 1. data-export 属性を付与（600px固定幅・縦積み・白背景CSS適用）
      el.setAttribute('data-export', '');

      // 2. html-to-image でキャプチャ（Retina 2x）
      //    モバイルでは外部画像の読み込みが初回に間に合わず空画像になる問題があるため、
      //    1回目の呼び出しで画像をキャッシュさせ、2回目で正しくキャプチャする
      const opts = { pixelRatio: EXPORT_PIXEL_RATIO, cacheBust: true };
      await toPng(el, opts); // 1回目: 画像キャッシュ用（結果は捨てる）
      const dataUrl = await toPng(el, opts);

      // 3. data-export 属性を除去 & video を復元
      el.removeAttribute('data-export');
      if (placeholderImg && videoEl && placeholderImg.parentElement) {
        placeholderImg.parentElement.replaceChild(videoEl, placeholderImg);
      }
      restoreRefVideos();

      // 4. ダウンロード
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      saveAs(blob, `generation-info_${ts}.png`);
    } catch {
      el.removeAttribute('data-export');
      if (placeholderImg && videoEl && placeholderImg.parentElement) {
        placeholderImg.parentElement.replaceChild(videoEl, placeholderImg);
      }
      restoreRefVideos();
    } finally {
      setExporting(false);
    }
  }, [exporting, videoThumbnailUrl]);

  return createPortal(
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* ヘッダー */}
        <div className={styles.header}>
          <h3 className={styles.title}>生成情報</h3>
          <div className={styles.headerActions}>
            {reusableLayout && generationState && layoutEditorEnabled && (
              <button
                type="button"
                className={styles.reuseLayoutButton}
                onClick={handleReuseLayout}
                title="この画像のレイアウト (配置と文言) を生成画面に読み込みます"
              >
                レイアウトを再利用
              </button>
            )}
            <button
              className={`${styles.exportButton} ${exporting ? styles.exportButtonLoading : ''}`}
              onClick={handleExport}
              disabled={exporting}
              aria-label="PNG画像として保存"
              title="PNG画像として保存"
            >
              {exporting ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M12 2a10 10 0 0 1 10 10" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              )}
            </button>
            <button className={styles.closeButton} onClick={onClose} aria-label="閉じる">
              &times;
            </button>
          </div>
        </div>

        {/* ボディ: 3カラムフロー */}
        <div className={styles.body}>
          <div className={styles.imageFlow} ref={flowRef} data-generation-flow>
            {/* 左カラム: 元素材 */}
            {hasSources ? (
              <div className={styles.sourceColumn}>
                <p className={styles.columnLabel}>{referenceVideos.length > 0 ? '生成元の素材' : '生成元の画像'}</p>
                <div className={styles.sourceImages}>
                  {referenceImages.map((ref, idx) => (
                    <div key={idx} className={styles.sourceImageItem}>
                      {/* 「選択素材」は自明なので種別ラベルを出さない (ロゴ等は出す) */}
                      {ref.label && ref.label !== '選択素材' && (
                        <span className={styles.sourceImageLabel}>{ref.label}</span>
                      )}
                      <div className={styles.sourceImageWrapper}>
                        <ImageWithFallback src={ref.url} alt={ref.label || `参照画像 ${idx + 1}`} />
                      </div>
                    </div>
                  ))}
                  {referenceVideos.map((url, idx) => (
                    <div key={`video-${idx}`} className={styles.sourceImageItem}>
                      <span className={styles.sourceImageLabel}>参照動画</span>
                      <div className={styles.sourceImageWrapper}>
                        <video src={url} controls preload="metadata" data-ref-video="" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className={styles.sourceColumn}>
                <p className={styles.columnLabel}>生成元の画像</p>
                <div className={styles.noSources}>元素材なし</div>
              </div>
            )}

            {/* 第1矢印: ソース → 属性（or 生成画像） */}
            {(hasSources || hasAttributes) && (
              <>
                <ArrowH />
                <ArrowV />
              </>
            )}

            {/* 中央カラム: 属性リスト */}
            {hasAttributes && (
              <>
                <div className={styles.attributesColumn}>
                  {/* カラムタイトル（動画+シーン情報がある場合はシナリオ名が代替するため非表示） */}
                  {!(isVideo && sceneEntries) && (
                    <p className={styles.columnLabel}>
                      {isVideo ? (directPrompt ? 'プロンプト' : '生成設定') : isAudio ? (directPrompt ? 'BGM 生成プロンプト' : '生成設定') : directPrompt ? 'プロンプト' : sceneEntries ? 'シーンスクリプト' : '使用属性'}
                    </p>
                  )}
                  {/* プロンプト入力モード: プロンプトテキストを表示 */}
                  {directPrompt && (
                    <div className={styles.attributeRows}>
                      <div className={styles.sceneRow}>
                        <span className={styles.sceneRowValue} style={{ whiteSpace: 'pre-wrap' }}>{directPrompt}</span>
                      </div>
                    </div>
                  )}
                  {/* 通常属性行（画像生成時のみ） */}
                  {!directPrompt && (attributes.length > 0 || actors.length > 0 || Boolean(sceneEntries)) && (
                    <div className={styles.attributeRows}>
                      {/* シーンプロンプト行（シナリオフレーム） */}
                      {sceneEntries && sceneEntries.map((entry, idx) => (
                        entry.key === 'シナリオ' ? (
                          <p key={idx} className={styles.columnLabel} style={{ marginBottom: 12 }}>{entry.value}</p>
                        ) : (
                          <div key={idx} className={styles.sceneRow}>
                            {entry.key && <span className={styles.sceneRowKey}>{entry.key}</span>}
                            <span className={styles.sceneRowValue}>{entry.value}</span>
                          </div>
                        )
                      ))}
                      {/* 通常属性行 */}
                      {attributes.map(({ key, label, value, section }, idx) => {
                        const isText = TEXT_ATTRIBUTE_KEYS.has(key);
                        const prevSection = idx > 0 ? attributes[idx - 1].section : section;
                        const isSectionBreak = idx > 0 && section !== prevSection;
                        const isFirstAfterScene = idx === 0 && Boolean(sceneEntries);
                        return (
                          <div key={key} className={`${styles.attributeRow} ${isText ? styles.attributeRowText : ''} ${isSectionBreak || isFirstAfterScene ? styles.attributeRowSectionBreak : ''}`}>
                            <span className={styles.attributeRowKey}>{label}</span>
                            <span className={styles.attributeRowValue}>{value}</span>
                          </div>
                        );
                      })}
                      {/* AI俳優行（最下行）。複数俳優は選択順 (登場人物A/B…) で全件表示。 */}
                      {actors.length > 0 && (
                        <div className={`${sceneEntries ? styles.sceneRow : styles.attributeRow} ${styles.attributeRowSectionBreak}`}>
                          <span className={sceneEntries ? styles.sceneRowKey : styles.attributeRowKey}>{brandConfig.labels.aiActor}</span>
                          <span className={styles.actorBadgeList}>
                            {actors.map((a) => (
                              <span key={a.id} className={styles.actorBadge}>
                                {a.faceUrl && (
                                  <img src={a.faceUrl} alt={a.name} className={styles.actorFaceCircle} />
                                )}
                                {a.name}
                              </span>
                            ))}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  {/* メタ情報（媒体要件・アスペクト比 / 動画生成設定） */}
                  {metaAttributes.length > 0 && (
                    <div className={`${styles.metaSection} ${isVideo && !sceneEntries && !directPrompt && actors.length === 0 ? styles.metaSectionVideoOnly : ''}`}>
                      {metaAttributes.map(({ key, label, value }) => (
                        <div key={key} className={styles.metaRow}>
                          <span className={styles.metaRowKey}>{label}</span>
                          <span className={styles.metaRowValue}>{value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* 適用コンプライアンスルール（薬機法・景表法等） */}
                  {hasCompliance && (
                    <div className={styles.complianceSection}>
                      <p className={styles.complianceSectionLabel}>適用コンプラ＆ルール</p>
                      <div className={styles.complianceBadges}>
                        {complianceRules.map((rule) => (
                          <span
                            key={rule.code || rule.name}
                            className={styles.complianceBadge}
                            title={rule.code}
                          >
                            {formatComplianceRuleName(rule.name)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* AI コンプライアンス判定結果 */}
                  {latestReport && (() => {
                    const verdictLabel = {
                      pass: '問題なし',
                      warn: '要注意',
                      block: '違反あり',
                      error: '判定エラー',
                    }[latestReport.overall_verdict] || latestReport.overall_verdict;
                    const verdictClass = {
                      pass: styles.verdictPass,
                      warn: styles.verdictWarn,
                      block: styles.verdictBlock,
                      error: styles.verdictError,
                    }[latestReport.overall_verdict] || '';
                    const ruleNameByCode = new Map(complianceRules.map((r) => [r.code, r.name]));
                    const violated = latestReport.violations.filter((v) => v.violated);
                    return (
                      <div className={styles.complianceSection}>
                        <p className={styles.complianceSectionLabel}>
                          AI コンプライアンス判定
                          <span className={`${styles.verdictBadge} ${verdictClass}`}>{verdictLabel}</span>
                        </p>
                        {latestReport.error_message ? (
                          <p className={styles.complianceNote}>判定に失敗しました: {latestReport.error_message}</p>
                        ) : violated.length === 0 ? (
                          <p className={styles.complianceNote}>違反は検出されませんでした</p>
                        ) : (
                          <div className={styles.complianceViolations}>
                            {violated.map((v, i) => (
                              <div key={`${v.rule_code}-${i}`} className={styles.complianceViolation}>
                                <div className={styles.complianceViolationHead}>
                                  <span
                                    className={`${styles.complianceBadge} ${
                                      v.severity === 'high'
                                        ? styles.complianceBadgeHigh
                                        : v.severity === 'low'
                                        ? styles.complianceBadgeLow
                                        : styles.complianceBadgeMid
                                    }`}
                                    title={v.rule_code}
                                  >
                                    {ruleNameByCode.get(v.rule_code) || v.rule_code}
                                  </span>
                                </div>
                                {v.evidence && (
                                  <p className={styles.complianceViolationEvidence}>{v.evidence}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        <p className={styles.complianceMeta}>
                          {latestReport.model}
                          {latestReport.evaluation_seconds != null && ` · ${latestReport.evaluation_seconds.toFixed(1)}秒`}
                        </p>
                      </div>
                    );
                  })()}
                </div>

                {/* 第2矢印: 属性 → 生成画像/動画 */}
                <ArrowH />
                <ArrowV />
              </>
            )}

            {/* 右カラム: 生成画像/動画 */}
            {generatedMediaUrl && (
              <div className={styles.generatedColumn}>
                <p className={styles.columnLabel}>{isVideo ? '生成された動画' : isAudio ? '生成された音声' : '生成された画像'}</p>
                <div className={styles.generatedImage}>
                  {isVideo ? (
                    <video src={generatedMediaUrl} controls data-generated-video="" />
                  ) : isAudio ? (
                    <audio src={generatedMediaUrl} controls data-generated-audio="" style={{ width: '100%' }} />
                  ) : (
                    <ImageWithFallback src={generatedMediaUrl} alt="生成画像" />
                  )}
                </div>
                {/* エクスポート時のみ表示されるロゴ */}
                <img
                  src="https://lirsarznxaidebqhgxch.supabase.co/storage/v1/object/public/media/sample/showhin_logo_watermark.png"
                  alt=""
                  className={styles.exportLogo}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
